// @ts-strict-ignore
import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { OrgOutlined } from '@/components/icons/iconHelpers';
import {
    AllNotificationTypes,
    displayTextForAppPlatform,
    iconForAppNotificationId,
    tooltipDescriptionsForEmailNotifs,
} from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Layout from 'antd/lib/layout/layout';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import {
    AppNotificationType,
    AppOrCommsNotificationSettings,
    BuildDistroBucketNotificationDetails,
    BuildDistroNotificationType,
    CommsNotificationDetails,
    EmailNotificationCategory,
    EmailNotificationType,
    NotificationSettingAppDetail,
    OrgNotificationSettings,
    UserEmailNotificationSettings,
} from '@/types';
import Icon, { AppstoreOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AppComponentProps } from '@/typescriptHelpers';
import { selectAllAppsIdsInOrg, selectApp, selectOrg } from '@/selectors/selectors';

import {
    useGetUserEmailNotificationSettingsQuery,
    useUpdateUserEmailNotificationSettingsMutation,
} from '@/middlewares/defaultApi';
import { Checkbox, CollapseProps } from 'antd/lib';
import Tooltip from 'antd/lib/tooltip';
import Row from 'antd/lib/row';
import Table from 'antd/lib/table';
import Collapse from 'antd/lib/collapse';
import Avatar from 'antd/lib/avatar';
import ConfigProvider from 'antd/lib/config-provider';
import FormItem from 'antd/lib/form/FormItem';
import Form from 'antd/lib/form';
import { globalMessage } from '@/utils/globalMessage';
import { BuildBucketAvatar } from '@/components/buildDistribution/CommonComponents';
import Empty from 'antd/lib/empty';
import Flex from 'antd/lib/flex';
import Result from 'antd/lib/result';
import Spin from 'antd/lib/spin';
import { useShouldShowNewOrgOverview } from '@/utils/internalFeatureFlags';

interface EmailNotificationSettingsFormProps extends AppComponentProps {
    emailNotificationSettings: UserEmailNotificationSettings;
}

const EmailNotificationSettingsForm: React.FC<EmailNotificationSettingsFormProps> = (props) => {
    const orgId = props.orgId;
    const emailNotificationSettings = props.emailNotificationSettings;
    const [form] = Form.useForm();
    const [buttonDisabled, setButtonDisabled] = React.useState(true);
    const [updateUserEmailNotifications, { isLoading: updateUserEmailNotificationsIsLoading }] =
        useUpdateUserEmailNotificationSettingsMutation();
    const allEmailNotificationTypes = Object.values(EmailNotificationType) as AllNotificationTypes[];

    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const shouldShowNewOrgOverview = useShouldShowNewOrgOverview(org);

    const appData = useSelector((state: RootState) => {
        const orgApps = selectAllAppsIdsInOrg(state, orgId);
        return orgApps.reduce((acc, appId) => {
            const appSettings = selectApp(state, appId).appSettings;
            if (appSettings) {
                acc[appId] = {
                    appIcon: appSettings.appImageUrl,
                    appName: appSettings.appName,
                    platform: appSettings.platform,
                };
            }
            return acc;
        }, {});
    }, shallowEqual);

    const appNotificationsData = emailNotificationSettings.notificationsDetails;

    const appNotificationTypes = Object.keys(appNotificationsData) as AllNotificationTypes[];
    appNotificationTypes.sort((a, b) => {
        return appNotificationsData[a].notification.displayText.localeCompare(
            appNotificationsData[b].notification.displayText,
        );
    });
    const orgNotificationTypes = Object.keys(emailNotificationSettings.userOrgNotificationPreferences);

    const [initialValues, setInitialValues] = React.useState({
        [EmailNotificationCategory.AppNotifications]: emailNotificationSettings.userAppNotificationPreferences,
        [EmailNotificationCategory.OrgNotifications]: emailNotificationSettings.userOrgNotificationPreferences,
        [EmailNotificationCategory.CommsNotifications]: emailNotificationSettings.userCommsNotificationPreferences,
    });
    // Function to handle save button state, compares values against each other in string form
    const setButtonState = (compareValues) => {
        setButtonDisabled(
            JSON.stringify(initialValues[EmailNotificationCategory.AppNotifications]) ===
                JSON.stringify(compareValues[EmailNotificationCategory.AppNotifications]) &&
                JSON.stringify(initialValues[EmailNotificationCategory.OrgNotifications]) ===
                    JSON.stringify(compareValues[EmailNotificationCategory.OrgNotifications]) &&
                JSON.stringify(initialValues[EmailNotificationCategory.CommsNotifications]) ===
                    JSON.stringify(compareValues[EmailNotificationCategory.CommsNotifications]),
        );
    };
    // Indeterminate state function for the check all or check none. Iterates thru all the app states to tally up disabled
    // and then return if the checkall should have indeterminate set or enabled or disabled
    const checkIndeterminateState = (apps, notificationId) => {
        if (apps && Object.keys(apps).length > 0) {
            const appStates = Object.keys(apps).reduce((acc, appId) => {
                const appData = apps[appId] as NotificationSettingAppDetail;
                const additionalData = appData.additionalData;

                let indeterminate = false;
                let checked = false;

                if (additionalData) {
                    const total = Object.keys(additionalData).length;
                    const checkedCount = Object.values(additionalData).filter(
                        (data: Record<string, BuildDistroBucketNotificationDetails>) => data.enabled,
                    ).length;
                    indeterminate = checkedCount > 0 && checkedCount < total;
                    checked = checkedCount === total;
                    acc[appId] = {
                        indeterminate: indeterminate,
                        checked: checked,
                        additionalData:
                            additionalData ?
                                Object.keys(additionalData).reduce((innerAcc, bucketId) => {
                                    innerAcc[bucketId] = { checked: additionalData[bucketId].enabled };
                                    return innerAcc;
                                }, {})
                            :   {},
                    };
                }
                return acc;
            }, {});

            const appLength = Object.keys(apps).length;
            let disabledCount: number;
            let indeterminate: boolean;
            if (notificationId !== BuildDistroNotificationType.NewBuildsAvailable) {
                disabledCount = Object.values(apps).filter((app) => {
                    const appData = app as NotificationSettingAppDetail;
                    return !appData.enabled;
                }).length;
                indeterminate = disabledCount > 0 && disabledCount !== appLength;
            } else {
                disabledCount = Object.values(appStates).filter((appState) => {
                    // @ts-ignore
                    return !appState.checked;
                }).length;
                indeterminate =
                    Object.values(appStates).filter((appState) => {
                        // @ts-ignore
                        return appState.indeterminate;
                    }).length > 0;
            }

            return {
                indeterminate: indeterminate,
                checked: disabledCount === 0,
                apps: appStates,
            };
        } else {
            return {
                indeterminate: false,
                checked: false,
                apps: {},
            };
        }
    };

    const [userAppNotificationState, setUserAppNotificationState] = React.useState(
        appNotificationTypes.reduce((acc, key) => {
            if (allEmailNotificationTypes.includes(key)) {
                acc[key] = checkIndeterminateState(
                    initialValues[EmailNotificationCategory.AppNotifications][key]?.apps,
                    key,
                );
            } else {
                acc[key] = checkIndeterminateState(
                    initialValues[EmailNotificationCategory.CommsNotifications][key]?.apps,
                    key,
                );
            }
            return acc;
        }, {}),
    );

    // When the checkall app box is clicked then we handle the state of all child app notifications underneath
    const handleNotificationIndeterminateStateAction = (e, key) => {
        const allChecked = e.target.checked;
        const category =
            allEmailNotificationTypes.includes(key) ?
                EmailNotificationCategory.AppNotifications
            :   EmailNotificationCategory.CommsNotifications;

        const currentApps = form.getFieldValue([category, key, 'apps']) || {};
        const updatedApps = (appNotificationsData[key].appIds ?? []).reduce((acc, appKey) => {
            const currentAppData = currentApps[appKey] || {};
            const updatedAdditionalData =
                currentAppData.additionalData ?
                    Object.keys(currentAppData.additionalData).reduce((innerAcc, bucketKey) => {
                        innerAcc[bucketKey] = { ...currentAppData.additionalData[bucketKey], enabled: allChecked };
                        return innerAcc;
                    }, {})
                :   {};
            acc[appKey] = {
                ...currentAppData,
                enabled: allChecked,
                additionalData: updatedAdditionalData,
            };
            return acc;
        }, {});
        form.setFieldsValue({
            [category]: {
                [key]: { apps: updatedApps },
            },
        });

        setUserAppNotificationState((prevState) => ({
            ...prevState,
            [key]: {
                indeterminate: false,
                checked: allChecked,
                apps: Object.keys(updatedApps).reduce((acc, appKey) => {
                    acc[appKey] = {
                        indeterminate: false,
                        checked: allChecked,
                        additionalData:
                            updatedApps[appKey].additionalData ?
                                Object.keys(updatedApps[appKey].additionalData).reduce((innerAcc, bucketKey) => {
                                    innerAcc[bucketKey] = { checked: allChecked };
                                    return innerAcc;
                                }, {})
                            :   {},
                    };
                    return acc;
                }, {}),
            },
        }));
    };

    // When the form changes then we update the state of the check all/check none
    const onFormValueChange = (allValues) => {
        // Create a new state object to hold updated indeterminate and checked values
        const newState = {};
        if (allValues) {
            const appFormValues = form.getFieldValue(EmailNotificationCategory.AppNotifications) as Record<
                EmailNotificationType,
                AppOrCommsNotificationSettings
            >;
            const commsFormValues = form.getFieldValue(EmailNotificationCategory.CommsNotifications) as Record<
                AppNotificationType | BuildDistroNotificationType,
                AppOrCommsNotificationSettings
            >;
            appNotificationTypes.forEach((notificationType) => {
                if (notificationType in appFormValues) {
                    newState[notificationType] = checkIndeterminateState(
                        appFormValues[notificationType].apps,
                        notificationType,
                    );
                } else if (notificationType in commsFormValues) {
                    newState[notificationType] = checkIndeterminateState(
                        commsFormValues[notificationType].apps,
                        notificationType,
                    );
                }
            });

            // Update the state in one go after the loop
            setUserAppNotificationState(newState);
        }
    };

    // save function on submit. Will transform data to fit request payload structure and submit a post request
    const saveSettings = async () => {
        const orgFormValues = form.getFieldValue(EmailNotificationCategory.OrgNotifications);
        const appFormValues = form.getFieldValue(EmailNotificationCategory.AppNotifications);
        const commsFormValues = form.getFieldValue(EmailNotificationCategory.CommsNotifications);
        const userAppNotificationPreferences = appNotificationTypes.reduce((acc, item) => {
            if (allEmailNotificationTypes.includes(item)) {
                acc[item] = {
                    type: EmailNotificationCategory.AppNotifications,
                    apps:
                        appFormValues[item] && appFormValues[item].apps ?
                            appFormValues[item].apps
                        :   (initialValues[item]?.apps ?? []),
                };
            }
            return acc;
        }, {});
        const userOrgNotificationPreferences = orgNotificationTypes.reduce((acc, item) => {
            acc[item] = {
                type: EmailNotificationCategory.OrgNotifications,
                enabled: orgFormValues[item].enabled,
            };
            return acc;
        }, {});
        const userCommsNotificationPreferences = appNotificationTypes.reduce((acc, item) => {
            if (!allEmailNotificationTypes.includes(item)) {
                const i = item as AppNotificationType | BuildDistroNotificationType;
                acc[i] = {
                    type: EmailNotificationCategory.CommsNotifications,
                    apps:
                        commsFormValues[i] && commsFormValues[i].apps ?
                            commsFormValues[i].apps
                        :   (initialValues[i]?.apps ?? []),
                };
            }
            return acc;
        }, {});
        const settings = {
            userAppNotificationPreferences,
            userOrgNotificationPreferences,
            userCommsNotificationPreferences,
        } as UserEmailNotificationSettings;

        const response = await updateUserEmailNotifications({ orgId, settings });
        if ('error' in response || response.data?.errorMessage) {
            globalMessage.error(
                ('data' in response && response.data?.errorMessage) || 'Error updating email notification settings',
            );
            return;
        }

        globalMessage.success('Email notification settings successfully updated!');

        setInitialValues({
            [EmailNotificationCategory.AppNotifications]: userAppNotificationPreferences as Record<
                EmailNotificationType,
                AppOrCommsNotificationSettings
            >,
            [EmailNotificationCategory.OrgNotifications]: userOrgNotificationPreferences as Record<
                EmailNotificationType,
                OrgNotificationSettings
            >,
            [EmailNotificationCategory.CommsNotifications]: userCommsNotificationPreferences as Record<
                AppNotificationType | BuildDistroNotificationType,
                AppOrCommsNotificationSettings
            >,
        });

        setButtonState(initialValues);
    };

    const saveEmailNotificationsButton = () => {
        return (
            <div
                style={{
                    borderRadius: '8px',
                    marginTop: 12,
                    display: 'flex',
                    gap: 12,
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ color: antGrey.grey8 }}>Manage the types of email notifications you receive</div>
                <Button
                    loading={updateUserEmailNotificationsIsLoading}
                    disabled={buttonDisabled}
                    icon={<CheckOutlined />}
                    type="primary"
                    htmlType="submit"
                >
                    Save email notification settings
                </Button>
            </div>
        );
    };

    const customBuildDistroBucketRow = (notificationId, category, appId, appIcon) => (record) => (
        <Row
            key={notificationId + record.bucket.id}
            align="middle"
            style={{
                marginRight: '32px',
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <div style={{ display: 'flex', gap: '12px' }}>
                <BuildBucketAvatar appImageUrl={appIcon} size={16} bucket={record.bucket} />
                <div>{record.bucket.name}</div>
            </div>
            <FormItem
                noStyle
                name={[category, notificationId, 'apps', appId, 'additionalData', record.bucket.id]}
                getValueFromEvent={(e) => {
                    return { enabled: e.target.checked };
                }}
                getValueProps={(value) => {
                    return { checked: value?.enabled || false };
                }}
                valuePropName="checked"
            >
                <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={
                            form.getFieldValue(category)?.[notificationId]?.apps?.[appId]?.additionalData?.[
                                record.bucket.id
                            ]?.enabled || false
                        }
                        onChange={(e) => {
                            const allChecked = e.target.checked;
                            const currentData = form.getFieldValue([category, notificationId, 'apps', appId]) || {};
                            const updatedAdditionalData = {
                                ...currentData.additionalData,
                                [record.bucket.id]: {
                                    ...currentData.additionalData[record.bucket.id],
                                    enabled: allChecked,
                                },
                            };
                            const anyAdditionalDataEnabled = Object.values(updatedAdditionalData).some(
                                (data: { enabled: boolean }) => data.enabled,
                            );
                            form.setFieldsValue({
                                [category]: {
                                    [notificationId]: {
                                        apps: {
                                            ...form.getFieldValue([category, notificationId, 'apps']),
                                            [appId]: {
                                                ...currentData,
                                                enabled: anyAdditionalDataEnabled,
                                                additionalData: updatedAdditionalData,
                                            },
                                        },
                                    },
                                },
                            });
                            setButtonState(form.getFieldsValue());
                        }}
                    />
                </div>
            </FormItem>
        </Row>
    );

    // row rendering for each of the individual app level notification choices
    const customAppLevelRow = (notificationId, category) => {
        const renderRow = (record, hasAdditionalData: boolean) => {
            const additionalData = form.getFieldValue([EmailNotificationCategory.CommsNotifications])?.[notificationId]
                ?.apps?.[record.name]?.additionalData;

            return (
                <Row
                    key={notificationId}
                    align="middle"
                    style={{
                        paddingRight: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Avatar
                            src={appData[record.name].appIcon}
                            shape="square"
                            size="small"
                            style={{ border: `1px solid ${antGrey.grey5}` }}
                        >
                            {record.name[0].toUpperCase()}
                        </Avatar>
                        <div>
                            {appData[record.name].appName} ({displayTextForAppPlatform(appData[record.name].platform)})
                        </div>
                    </div>
                    <FormItem
                        noStyle
                        name={[category, notificationId, 'apps', record.name]}
                        getValueFromEvent={(e) => {
                            const currentData =
                                form.getFieldValue([category, notificationId, 'apps', record.name]) || {};
                            return { ...currentData, enabled: e.target.checked };
                        }}
                        getValueProps={(value) => {
                            return { checked: value?.enabled || false };
                        }}
                        valuePropName="checked"
                    >
                        {hasAdditionalData ?
                            <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    indeterminate={
                                        userAppNotificationState[notificationId]?.apps[record.name]?.indeterminate
                                    }
                                    checked={userAppNotificationState[notificationId]?.apps[record.name]?.checked}
                                    onChange={(e) => {
                                        const allChecked = e.target.checked;
                                        const updatedAdditionalData = Object.keys(additionalData).reduce((acc, key) => {
                                            acc[key] = { ...additionalData[key], enabled: allChecked };
                                            return acc;
                                        }, {});
                                        const currentApps = userAppNotificationState[notificationId]?.apps || {};
                                        form.setFieldsValue({
                                            [category]: {
                                                [notificationId]: {
                                                    apps: {
                                                        ...currentApps,
                                                        [record.name]: {
                                                            enabled: allChecked,
                                                            additionalData: updatedAdditionalData,
                                                        },
                                                    },
                                                },
                                            },
                                        });
                                        setButtonState(form.getFieldsValue());
                                    }}
                                />
                            </div>
                        :   <Checkbox
                                checked={
                                    form.getFieldValue([category, notificationId, 'apps', record.name])?.enabled ||
                                    false
                                }
                                onChange={(e) => {
                                    const currentData =
                                        form.getFieldValue([category, notificationId, 'apps', record.name]) || {};
                                    const currentApps = form.getFieldValue([category, notificationId, 'apps']) || {};
                                    form.setFieldsValue({
                                        [category]: {
                                            [notificationId]: {
                                                apps: {
                                                    ...currentApps,
                                                    [record.name]: { ...currentData, enabled: e.target.checked },
                                                },
                                            },
                                        },
                                    });
                                    setButtonState(form.getFieldsValue());
                                }}
                            />
                        }
                    </FormItem>
                </Row>
            );
        };

        return [
            {
                render: (_, record) =>
                    additionalDataForNotificationForApp(appNotificationsData, notificationId, record.name) ?
                        <Collapse
                            bordered={false}
                            defaultActiveKey={record.name}
                            items={[
                                {
                                    key: record.name,
                                    label: renderRow(record, true),
                                    children: (
                                        <div
                                            style={{
                                                display: 'block',
                                                borderRadius: 8,
                                                backgroundColor: antGrey.grey3,
                                                color: runwayColors.darkTextGrey,
                                                margin: '0px -16px -17px 48px',
                                            }}
                                        >
                                            <Table
                                                style={{
                                                    marginLeft: '0px',
                                                    borderTop: `1px solid ${antGrey.grey5}`,
                                                    borderLeft: `1px solid ${antGrey.grey5}`,
                                                    borderBottom: 'none !important',
                                                    height: '100%',
                                                    marginBottom: '-1px',
                                                }}
                                                locale={{
                                                    emptyText: <Empty description="No buckets for app" />,
                                                }}
                                                bordered={false}
                                                showHeader={false}
                                                pagination={false}
                                                dataSource={Object.values(
                                                    additionalDataForNotificationForApp(
                                                        appNotificationsData,
                                                        notificationId,
                                                        record.name,
                                                    ),
                                                )
                                                    .sort((a, b) => {
                                                        return a.name.localeCompare(b.name);
                                                    })
                                                    .map((bucket) => ({
                                                        key: bucket.id,
                                                        name: bucket.name,
                                                        bucket: bucket,
                                                    }))}
                                                columns={[
                                                    {
                                                        render: customBuildDistroBucketRow(
                                                            notificationId,
                                                            category,
                                                            record.name,
                                                            appData[record.name].appIcon,
                                                        ),
                                                    },
                                                ]}
                                            />
                                        </div>
                                    ),
                                },
                            ]}
                            style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                marginLeft: '-16px',
                                marginRight: '-24px',
                            }}
                        />
                    :   renderRow(record, false),
            },
        ];
    };

    // Making a copy first before sorting since the array is immutable due to RTK response
    const sortedAppIdsForNotification = (notificationType: AllNotificationTypes) =>
        [...(appNotificationsData[notificationType].appIds ?? [])].sort();

    // Collapse panel for each of the app notifications
    const appCollapseItems: CollapseProps['items'] = appNotificationTypes.map((item) => ({
        key: item,
        label: (
            <div
                style={{
                    overflow: 'hidden',
                    display: 'flex',
                    gap: '12px',
                }}
            >
                <Icon
                    component={iconForAppNotificationId(item)}
                    style={{
                        fontSize: 16,
                        color: antGrey.grey7,
                        paddingTop: 4,
                    }}
                />
                <div style={{ color: runwayColors.darkTextGrey }}>
                    {appNotificationsData[item].notification.displayText}
                </div>
                <div>
                    {appNotificationsData[item].notification.info && (
                        <Tooltip
                            title={appNotificationsData[item].notification.info}
                            key={appNotificationsData[item].notification.info}
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                        </Tooltip>
                    )}
                </div>
            </div>
        ),
        extra: (
            <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    indeterminate={userAppNotificationState[item]?.indeterminate}
                    checked={userAppNotificationState[item].checked}
                    onChange={(e) => {
                        handleNotificationIndeterminateStateAction(e, item);
                        setButtonState(form.getFieldsValue());
                    }}
                />
            </div>
        ),
        children: (
            <div
                style={{
                    overflow: 'hidden',
                    color: runwayColors.darkTextGrey,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <div
                    style={{
                        display: 'inline',
                    }}
                >
                    <Table
                        bordered={false}
                        style={{
                            marginLeft: '48px',
                            borderLeft: `1px solid ${antGrey.grey5}`,
                            borderBottom: 'none !important',
                            height: '100%',
                            marginBottom: '-1px',
                        }}
                        locale={{
                            emptyText: <Empty description="No apps for user or notification" />,
                        }}
                        showHeader={false}
                        pagination={false}
                        dataSource={sortedAppIdsForNotification(item).map((appId, index) => ({
                            key: index.toString(),
                            name: appId,
                            additionalData: additionalDataForNotificationForApp(appNotificationsData, item, appId),
                        }))}
                        columns={customAppLevelRow(item, appNotificationsData[item].type)}
                    />
                </div>
            </div>
        ),
    }));

    return (
        <Layout>
            <div
                style={{
                    marginBottom: '20px',
                    height: 'fit-content',
                }}
            >
                <Form
                    form={form}
                    initialValues={initialValues}
                    onValuesChange={(_, allValues) => {
                        onFormValueChange(allValues);
                        setButtonState(allValues);
                    }}
                    onFinish={saveSettings}
                >
                    <div>{saveEmailNotificationsButton()}</div>

                    <ConfigProvider
                        theme={{
                            components: {
                                Collapse: {
                                    contentPadding: 0,
                                    headerPadding: '14px 24px 14px 24px',
                                },
                                Table: {
                                    borderColor: antGrey.grey5,
                                    borderRadius: 8,
                                },
                            },
                            token: {
                                borderRadius: 0,
                            },
                        }}
                    >
                        <div style={{ paddingBottom: '20px' }}>
                            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <Icon component={OrgOutlined} style={{ fontSize: '10px', color: antGrey.grey6 }} />
                                    Organization-level notifications
                                </div>
                                <Divider style={{ marginTop: '5px', marginBottom: '5px' }} />
                            </div>
                            <div
                                style={{
                                    display: 'block',
                                    borderRadius: 8,
                                    backgroundColor: antGrey.grey3,
                                    border: `1px solid ${antGrey.grey5}`,
                                    color: runwayColors.darkTextGrey,
                                }}
                            >
                                {/*Org notifications*/}
                                {orgNotificationTypes
                                    .filter(
                                        (key) =>
                                            shouldShowNewOrgOverview ||
                                            key !== EmailNotificationType.OrgOverviewSummary,
                                    )
                                    .map((key, notifIndex) => (
                                        <div key={key}>
                                            <div
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: '14px 24px 14px 24px',
                                                    justifyContent: 'space-between',
                                                    display: 'flex',
                                                    borderTop: notifIndex !== 0 ? `1px solid ${antGrey.grey5}` : '',
                                                }}
                                            >
                                                <div style={{ display: 'flex', gap: '12px' }}>
                                                    <Icon
                                                        component={iconForAppNotificationId(
                                                            key as EmailNotificationType,
                                                        )}
                                                        style={{
                                                            fontSize: 16,
                                                            color: antGrey.grey7,
                                                            paddingTop: 4,
                                                        }}
                                                    />
                                                    <div>{titleForEmailNotificationType(key)}</div>
                                                    <div>
                                                        {tooltipDescriptionsForEmailNotifs[key] && (
                                                            <Tooltip
                                                                title={tooltipDescriptionsForEmailNotifs[key]}
                                                                key={tooltipDescriptionsForEmailNotifs[key]}
                                                            >
                                                                <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>
                                                <FormItem
                                                    noStyle
                                                    name={[EmailNotificationCategory.OrgNotifications, key, 'enabled']}
                                                    valuePropName="checked"
                                                >
                                                    <Checkbox />
                                                </FormItem>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        {/*App notifications*/}

                        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '10px',
                                    alignItems: 'center',
                                }}
                            >
                                <AppstoreOutlined style={{ color: antGrey.grey6 }} />
                                App-level notifications
                            </div>
                            <Divider style={{ marginTop: '5px', marginBottom: '5px' }} />
                        </div>

                        <div
                            style={{
                                display: 'block',
                                borderRadius: 8,
                                backgroundColor: antGrey.grey3,
                                color: runwayColors.darkTextGrey,
                            }}
                        >
                            <Collapse
                                bordered
                                defaultActiveKey={[]}
                                items={appCollapseItems}
                                style={{
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    backgroundColor: antGrey.grey3,
                                    color: runwayColors.darkTextGrey,
                                }}
                            />
                        </div>
                    </ConfigProvider>
                </Form>
            </div>
        </Layout>
    );
};

const EmailNotificationSettings: React.FC<AppComponentProps> = (props) => {
    const { data, isLoading, refetch } = useGetUserEmailNotificationSettingsQuery(
        { orgId: props.orgId },
        // Overriding default behavior since user settings can't be updated anywhere else
        { refetchOnFocus: false },
    );

    if (isLoading) {
        return (
            <Flex align="center" justify="center" style={{ minHeight: 200 }}>
                <Spin size="large" />
            </Flex>
        );
    }

    if (!data?.data) {
        return (
            <Result
                status="warning"
                subTitle={data?.errorMessage ?? 'Error fetching email notification settings'}
                extra={<Button onClick={() => refetch()}>Try again</Button>}
            />
        );
    }

    return <EmailNotificationSettingsForm {...props} emailNotificationSettings={data.data} />;
};

export default EmailNotificationSettings;

const titleForEmailNotificationType: (type: string) => string = (type) => {
    switch (type) {
        case EmailNotificationType.OrgMetricsDigest:
            return 'Org metrics digest';
        case EmailNotificationType.OrgReleasesDigest:
            return 'Org app releases digest';
        case EmailNotificationType.OrgOverviewSummary:
            return 'Org overview summary';
        case EmailNotificationType.AppReleaseDigest:
            return 'App release digest';
        case EmailNotificationType.ReleaseSummary:
            return 'Release summary';
        default:
            return '';
    }
};

const additionalDataForNotificationForApp: (
    data: Record<AllNotificationTypes, CommsNotificationDetails>,
    item: AllNotificationTypes,
    appId: string,
) => Record<string, BuildDistroBucketNotificationDetails> = (
    data: Record<AllNotificationTypes, CommsNotificationDetails>,
    item: AllNotificationTypes,
    appId: string,
) => {
    return data[item]?.additionalDataForApp ?
            (data[item]?.additionalDataForApp[appId] as Record<string, BuildDistroBucketNotificationDetails>)
        :   null;
};
